import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Skeleton from '@mui/material/Skeleton';
import { apiUrl, fetchJson } from '../apiBase';

const MaterialStatus = ({ onError, schoolId }) => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  const notifyError = useCallback((msg) => { onErrorRef.current && onErrorRef.current(msg); }, []);

  const normalizeClassCode = useCallback((value) => {
    return String(value ?? '')
      .replace(/\u00A0/g, ' ')
      .trim()
      .toLocaleUpperCase('tr-TR');
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!schoolId) return;
      setLoadingClasses(true);
      try {
        const res = await fetchJson(apiUrl(`/admin/classes/${encodeURIComponent(schoolId)}`));
        if (!res.ok) throw new Error('classes');
        const data = await res.json();
        const items = (data || [])
          .map(c => ({
            id: c?.id,
            name: normalizeClassCode(c?.name),
          }))
          .filter(x => x?.id != null && x?.name);
        items.sort((a, b) => String(a.name).localeCompare(String(b.name), 'tr-TR'));
        setClasses(items);
        setSelectedClass(prev => prev || (items[0] || null));
      } catch (e) {
        notifyError('Şubeler yüklenemedi');
      } finally {
        setLoadingClasses(false);
      }
    };
    run();
  }, [schoolId, notifyError, normalizeClassCode]);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const classId = selectedClass?.id;
      const statusUrl = classId
        ? `/admin/materials-status?school_id=${encodeURIComponent(schoolId)}&class_id=${encodeURIComponent(classId)}`
        : `/admin/materials-status?school_id=${encodeURIComponent(schoolId)}`;

      const [sumRes, statusRes] = await Promise.all([
        fetchJson(apiUrl(`/admin/materials-summary?school_id=${encodeURIComponent(schoolId)}`)),
        fetchJson(apiUrl(statusUrl)),
      ]);

      if (sumRes.ok) {
        setSummary(await sumRes.json());
      } else {
        setSummary(null);
      }

      if (!statusRes.ok) throw new Error('status');
      const data = await statusRes.json();
      setRows((data?.materials || []).slice(0, 500));
    } catch (e) {
      notifyError('Materyal durumu yüklenemedi');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClass, notifyError]);

  useEffect(() => { load(); }, [load]);

  const cards = useMemo(() => {
    const s = summary || {};
    return [
      { label: 'Toplam Öğrenci', value: s.total_students ?? '-' },
      { label: 'Bugün Tam', value: s.complete_today ?? '-' },
      { label: 'Bugün Eksik', value: s.missing_today ?? '-' },
      { label: 'Eksik Oranı', value: (s.missing_rate ?? '-') + (typeof s.missing_rate === 'number' ? '%' : '') },
    ];
  }, [summary]);

  return (
    <>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <Autocomplete
              size="small"
              options={classes}
              value={selectedClass}
              getOptionLabel={(opt) => String(opt?.name || '')}
              onChange={(_, v) => setSelectedClass(v || null)}
              loading={loadingClasses}
              renderInput={(params) => <TextField {...params} label="Şube" placeholder="Seçin" fullWidth />}
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {cards.map((c) => (
          <Grid key={c.label} item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary">{c.label}</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{c.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>Kayıt Geçmişi</Typography>
        {loading ? (
          <Box>
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} variant="text" width="90%" />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
            <Typography>Kayıt bulunamadı.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tarih</TableCell>
                <TableCell>Öğrenci</TableCell>
                <TableCell>Durum</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={`${r.student_id}-${r.date}-${idx}`}>
                  <TableCell>{String(r.date || '')}</TableCell>
                  <TableCell>{String(r.student_name || '')}</TableCell>
                  <TableCell>{String(r.status || '')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </>
  );
};

export default MaterialStatus;
